import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, X, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

interface SizeRow {
    name: string;
    in_use_count: number;
}

const LocationSizesAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [sizes, setSizes] = useState<SizeRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);
    const [pendingRemove, setPendingRemove] = useState<SizeRow | null>(null);
    const [removing, setRemoving] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/put-away/location-sizes`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: SizeRow[] = await res.json();
            setSizes(data);
        } catch {
            toast.error(t("server_error"), { toastId: "loc-sizes-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        setAdding(true);
        try {
            const res = await apiFetch(`/put-away/location-sizes`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name }),
            });
            if (res.status === 409) {
                toast.error(t("locations_size_exists"), { toastId: "loc-size-dup" });
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setNewName("");
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "loc-size-add-err" });
        } finally {
            setAdding(false);
        }
    };

    const confirmRemove = async () => {
        if (!pendingRemove) return;
        setRemoving(true);
        try {
            const res = await apiFetch(
                `/put-away/location-sizes/${encodeURIComponent(pendingRemove.name)}`,
                { method: "DELETE" },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setPendingRemove(null);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "loc-size-remove-err" });
        } finally {
            setRemoving(false);
        }
    };

    if (loading) {
        return <div className="admin-settings-loading">{t("loading")}</div>;
    }

    return (
        <div className="admin-settings-po-editor">
            <div className="dispatch-admin-rows">
                {sizes.map(s => {
                    const inUse = s.in_use_count > 0;
                    return (
                        <div key={s.name} className="dispatch-admin-row dispatch-admin-row--desk">
                            <span className="dispatch-admin-row-name">{s.name}</span>
                            <span className="dispatch-admin-row-barcode">
                                {t("locations_size_in_use", { count: String(s.in_use_count) })}
                            </span>
                            <div className="dispatch-admin-row-actions-inline">
                                <button
                                    type="button"
                                    className="dispatch-admin-row-icon dispatch-admin-row-icon--danger"
                                    onClick={() => setPendingRemove(s)}
                                    disabled={inUse || readOnly}
                                    aria-disabled={readOnly}
                                    title={inUse ? t("locations_size_remove_blocked_title") : (readOnly ? t("read_only_mode") : t("remove"))}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    );
                })}
                {sizes.length === 0 && (
                    <div className="admin-settings-empty">{t("locations_no_sizes_yet")}</div>
                )}
            </div>
            <div className="dispatch-admin-row-actions" style={{ marginTop: 12, gap: 8 }}>
                <input
                    type="text"
                    className="dispatch-admin-form-input"
                    style={{ flex: "1 1 200px", maxWidth: 280 }}
                    placeholder={t("locations_size_name_placeholder")}
                    value={newName}
                    maxLength={64}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
                    disabled={adding || readOnly}
                />
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleAdd}
                    disabled={adding || !newName.trim() || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    {adding ? <Loader2 size={14} className="dispatch-admin-spin" /> : <Plus size={14} />}
                    {t("locations_size_add")}
                </button>
            </div>

            {pendingRemove && (
                <div
                    className="modal-overlay"
                    onClick={() => !removing && setPendingRemove(null)}
                >
                    <div
                        className="modal-card locations-delete-modal"
                        onClick={e => e.stopPropagation()}
                        role="alertdialog"
                        aria-labelledby="loc-size-remove-title"
                    >
                        <div className="modal-card-header">
                            <h2 id="loc-size-remove-title">{t("locations_size_remove_title")}</h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setPendingRemove(null)}
                                disabled={removing}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="locations-delete-body">
                                {t("locations_size_remove_body", { name: pendingRemove.name })}
                            </p>
                            {pendingRemove.in_use_count > 0 && (
                                <p
                                    className="admin-settings-section-hint admin-settings-section-hint--danger"
                                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                                >
                                    <AlertTriangle size={16} />
                                    {t("locations_size_remove_in_use", {
                                        count: String(pendingRemove.in_use_count),
                                    })}
                                </p>
                            )}
                            <p className="locations-delete-hint">{t("locations_size_remove_hint")}</p>
                        </div>
                        <div className="modal-confirm-buttons">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setPendingRemove(null)}
                                disabled={removing}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmRemove}
                                disabled={removing || readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                <Trash2 size={16} />
                                {removing ? t("submitting") : t("remove")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationSizesAdmin;
